import React, { useState } from "react";
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";

import classNames from "classnames";
import { capitalize } from "lodash";
import invariant from "tiny-invariant";

import EditUmpires from "./EditUmpires";

import { alertActions } from "../../../../features/alert/alertSlice";
import { selectSelectedTournament } from "../../../../features/tourneymgmt/tournamentManagementSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import { updateGame } from "../../../../lib/api";
import { AUTH_LEVELS } from "../../../../lib/globals";
import { isChildTournament } from "../../../../lib/utils";
import { ITeam, IUmpire } from "../../../../types/api";
import { ICalendarEvent } from "../../../../types/shared";

interface ModalProps extends ICalendarEvent {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    setShowDelete: React.Dispatch<React.SetStateAction<boolean>>;
    setEventToDelete: React.Dispatch<
        React.SetStateAction<ICalendarEvent | undefined>
    >;
    events: Array<ICalendarEvent>;
    setEvents: React.Dispatch<React.SetStateAction<ICalendarEvent[]>>;
    resources: {
        resourceTitle: string;
        resourceId: number;
    }[];
}

const EditGameModal = ({
    show,
    setShow,
    setShowDelete,
    setEventToDelete,
    ageLevel,
    start,
    resourceId,
    resources,
    id: gameId,
    umpires,
    home,
    visitor,
    setEvents,
    end,
    title,
}: ModalProps) => {
    const dispatch = useAppDispatch();
    const tournament = useAppSelector(selectSelectedTournament);
    const tournaments = useAppSelector(
        state => state.tournamentmanagement.tournaments,
    );
    const user = useAppSelector(state => state.auth.user);
    invariant(user);
    invariant(tournament);

    const [showChangeTeams, setShowChangeTeams] = useState(false);
    const [showScoreEntry, setShowScoreEntry] = useState(false);
    const [showEditUmpires, setShowEditUmpires] = useState(false);
    const [homeTeam, setHomeTeam] = useState<number | undefined>(home?.id);
    const [visitorTeam, setVisitorTeam] = useState<number | undefined>(
        visitor?.id,
    );
    const [showHomeCoachInfo, setShowHomeCoachInfo] = useState<boolean>(false);
    const [showVisitorCoachInfo, setShowVisitorCoachInfo] =
        useState<boolean>(false);
    const [homeScore, setHomeScore] = useState<number | undefined>(home?.score);
    const [visitorScore, setVisitorScore] = useState<number | undefined>(
        visitor?.score,
    );
    const [plateUmpire, setPlateUmpire] = useState<number>();
    const [fieldUmpire, setFieldUmpire] = useState<number>();
    const [showPlateUmpireInfo, setShowPlateUmpireInfo] =
        useState<boolean>(false);
    const [showFieldUmpireInfo, setShowFieldUmpireInfo] =
        useState<boolean>(false);

    const tournamentTeams = !isChildTournament(tournament)
        ? Array.from(
              new Map(
                  tournaments
                      .filter(t => t.parent === tournament.id)
                      .filter(t => t.ageLevel === ageLevel)
                      .flatMap(t => t.teams)
                      .filter((t): t is ITeam => !!t)
                      .map(t => [t?.id, { value: t?.id, label: t.name }]),
              ).values(),
          )
        : tournament.teams?.map(t => ({ value: t.id, label: t.name }));

    const handleSubmit = async () => {
        updateGame(
            {
                fieldUmpire,
                plateUmpire,
                scores: { home: homeScore, visitor: visitorScore },
                teams: { home: homeTeam, visitor: visitorTeam },
            },
            gameId,
            user.id,
        )
            .then(response => {
                const { game } = response;
                setEvents(prev => {
                    return prev.map(e => {
                        return e.id === game.id
                            ? {
                                  ...e,
                                  home: game.home,
                                  visitor: game.visitor,
                                  umpires: game.umpires,
                                  title: `${game.visitor.name || "TBD"} vs ${game.home.name || "TBD"}`,
                              }
                            : e;
                    });
                });
                dispatch(
                    alertActions.success({
                        title: `Game updated successfully`,
                        dismissible: true,
                        scroll: false,
                    }),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: `The server encountered a updating the game.`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            })
            .finally(() => setShow(false));
    };

    const showTeamInfo = (teamType: "home" | "visitor"): React.ReactNode => {
        const team = teamType === "home" ? home : visitor;
        const opp = teamType === "home" ? visitor : home;
        const showCoachInfo =
            teamType === "home" ? showHomeCoachInfo : showVisitorCoachInfo;
        const setShowCoachInfo =
            teamType === "home"
                ? setShowHomeCoachInfo
                : setShowVisitorCoachInfo;

        return team?.name ? (
            team.score && opp?.score ? (
                parseInt(team.score.toString()) >
                parseInt(opp?.score.toString()) ? (
                    <React.Fragment>
                        <b>
                            <span
                                className={classNames("link-like", "h5")}
                                onClick={e => setShowCoachInfo(!showCoachInfo)}
                            >
                                {team.name}
                                {!showScoreEntry ? ` - ${team.score}` : ""}
                            </span>
                        </b>
                    </React.Fragment>
                ) : (
                    <span
                        className={classNames("link-like", "h5")}
                        style={{ fontWeight: "normal" }}
                        onClick={e => setShowCoachInfo(!showCoachInfo)}
                    >
                        {team.name}
                        {!showScoreEntry ? ` - ${team.score}` : ""}
                    </span>
                )
            ) : (
                <span
                    className={classNames("link-like", "h5")}
                    style={{ fontWeight: "normal" }}
                    onClick={e => setShowCoachInfo(!showCoachInfo)}
                >
                    {team.name}
                </span>
            )
        ) : (
            "TBD"
        );
    };

    const showUmpireInfo = (umpire: IUmpire): React.ReactNode => {
        const showInfo =
            umpire.location === "plate"
                ? showPlateUmpireInfo
                : showFieldUmpireInfo;
        const setShowInfo =
            umpire.location === "plate"
                ? setShowPlateUmpireInfo
                : setShowFieldUmpireInfo;

        const hasPlate = umpires?.find(u => u.location === "plate");
        return (
            <React.Fragment>
                {umpire.location === "field" &&
                    hasPlate &&
                    !showPlateUmpireInfo && <br />}
                <span
                    className={classNames("link-like")}
                    onClick={e => setShowInfo(!showInfo)}
                >
                    {capitalize(umpire.location)}: {umpire.name}
                </span>
                {showInfo && (
                    <div
                        className={classNames({
                            "border-bottom": umpire.location === "plate",
                            "pb-1": umpire.location === "field",
                            "mb-1": umpire.location === "field",
                            "pb-2": umpire.location === "plate",
                            "mb-2": umpire.location === "plate",
                        })}
                    >
                        <b>Email:</b>{" "}
                        {umpire.email ? (
                            <a href={`mailto:${umpire.email}`}>
                                {umpire.email}
                            </a>
                        ) : (
                            "no email avaialable"
                        )}
                        <br />
                        <b>Phone:</b>{" "}
                        {umpire.phone ? (
                            <a href={`tel:${umpire.phone.replace(/\D+/g, "")}`}>
                                {umpire.phone}
                            </a>
                        ) : (
                            "no phone available"
                        )}
                    </div>
                )}
            </React.Fragment>
        );
    };

    return (
        <Modal show={show} onHide={() => setShow(false)} centered>
            <Modal.Header>
                <Modal.Title>Update/Submit Game Info</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    <Row>
                        <Col xs={3}>
                            <b>Date:</b>
                        </Col>
                        <Col>
                            {new Date(start).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                            })}{" "}
                            @{" "}
                            {new Date(start).toLocaleTimeString("en-US", {
                                hour: "numeric",
                                hour12: true,
                                minute: "2-digit",
                            })}
                        </Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={3}>
                            <b>Field:</b>
                        </Col>
                        <Col xs={3}>
                            {
                                resources.find(r => r.resourceId === resourceId)
                                    ?.resourceTitle
                            }
                        </Col>
                        <Col xs={3}>
                            <b>Age Level:</b>
                        </Col>
                        <Col xs={3}>{ageLevel}</Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={3}>
                            <b>Teams:</b>
                        </Col>
                        <Col
                            xs={6}
                            className={classNames({
                                "border-bottom": showVisitorCoachInfo,
                            })}
                        >
                            {showChangeTeams ? (
                                <Select
                                    isSearchable={false}
                                    isClearable={false}
                                    isMulti={false}
                                    options={tournamentTeams
                                        ?.filter(t => t.value !== homeTeam)
                                        .map(t => ({
                                            value: t.value,
                                            label: t.label,
                                        }))}
                                    defaultValue={
                                        home
                                            ? tournamentTeams?.find(
                                                  t => t.value === visitor?.id,
                                              )
                                            : undefined
                                    }
                                    onChange={t => setVisitorTeam(t?.value)}
                                />
                            ) : (
                                showTeamInfo("visitor")
                            )}
                            {showVisitorCoachInfo && (
                                <React.Fragment>
                                    <br />
                                    <b>Email:</b>{" "}
                                    {visitor?.coach.email ? (
                                        <a
                                            href={`mailto:${visitor.coach.email}`}
                                        >
                                            {visitor.coach.email}
                                        </a>
                                    ) : (
                                        "no email avaialable"
                                    )}
                                    <br />
                                    <b>Phone:</b>{" "}
                                    {visitor?.coach.phone ? (
                                        <a
                                            href={`tel:${visitor.coach.phone.replace(/\D+/g, "")}`}
                                        >
                                            {visitor.coach.phone}
                                        </a>
                                    ) : (
                                        "no phone available"
                                    )}
                                </React.Fragment>
                            )}
                        </Col>
                        <Col
                            xs={3}
                            className={classNames("text-right", "px-0")}
                        >
                            {!showScoreEntry ? (
                                <Button
                                    size="sm"
                                    onClick={() => setShowScoreEntry(true)}
                                >
                                    <small>Submit Score?</small>
                                </Button>
                            ) : (
                                <Form.Control
                                    name="visitorScore"
                                    defaultValue={visitorScore}
                                    onChange={e =>
                                        setVisitorScore(
                                            parseInt(e.currentTarget.value),
                                        )
                                    }
                                    placeholder="Score..."
                                />
                            )}
                        </Col>
                    </Row>
                    <Row className={classNames("mt-1")}>
                        <Col
                            xs={{ span: 6, offset: 3 }}
                            className={classNames({
                                "border-bottom": showHomeCoachInfo,
                            })}
                        >
                            {showChangeTeams ? (
                                <Select
                                    isSearchable={false}
                                    isClearable={false}
                                    isMulti={false}
                                    options={tournamentTeams
                                        ?.filter(t => t.value !== visitorTeam)
                                        ?.map(t => ({
                                            value: t.value,
                                            label: t.label,
                                        }))}
                                    defaultValue={
                                        home
                                            ? tournamentTeams?.find(
                                                  t => t.value === home?.id,
                                              )
                                            : undefined
                                    }
                                    onChange={t => setHomeTeam(t?.value)}
                                />
                            ) : (
                                showTeamInfo("home")
                            )}
                            {showHomeCoachInfo && (
                                <React.Fragment>
                                    <br />
                                    <b>Email:</b>{" "}
                                    {home?.coach.email ? (
                                        <a href={`mailto:${home.coach.email}`}>
                                            {home.coach.email}
                                        </a>
                                    ) : (
                                        "no email avaialable"
                                    )}
                                    <br />
                                    <b>Phone:</b>{" "}
                                    {home?.coach.phone ? (
                                        <a
                                            href={`tel:${home.coach.phone.replace(/\D+/g, "")}`}
                                        >
                                            {home.coach.phone}
                                        </a>
                                    ) : (
                                        "no phone available"
                                    )}
                                </React.Fragment>
                            )}
                        </Col>
                        <Col
                            xs={3}
                            className={classNames("text-right", "px-0")}
                        >
                            {showScoreEntry && (
                                <Form.Control
                                    name="homeScore"
                                    defaultValue={homeScore}
                                    onChange={e =>
                                        setHomeScore(
                                            parseInt(e.currentTarget.value),
                                        )
                                    }
                                    placeholder="Score..."
                                />
                            )}
                            {!showChangeTeams &&
                                AUTH_LEVELS.sitemanagement.includes(
                                    user!.userLevel,
                                ) && (
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            setShowChangeTeams(true);
                                        }}
                                    >
                                        <small>Change Teams?</small>
                                    </Button>
                                )}
                        </Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={3}>
                            <b>Umpires:</b>
                        </Col>
                        <Col xs={showEditUmpires ? 9 : 6}>
                            {!showEditUmpires ? (
                                umpires
                                    ?.sort((a, b) => {
                                        if (a.location === "plate") return -1;
                                        if (b.location === "plate") return 1;
                                        return 0;
                                    })
                                    .map(u => {
                                        return (
                                            <React.Fragment key={u.id}>
                                                {showUmpireInfo(u)}
                                            </React.Fragment>
                                        );
                                    })
                            ) : (
                                <EditUmpires
                                    umpires={umpires}
                                    setPlateUmpire={setPlateUmpire}
                                    setFieldUmpire={setFieldUmpire}
                                    ageLevel={ageLevel}
                                />
                            )}
                        </Col>
                        {!showEditUmpires && (
                            <Col
                                xs={3}
                                className={classNames("text-right", "px-0")}
                            >
                                <Button
                                    size="sm"
                                    onClick={() => setShowEditUmpires(true)}
                                >
                                    <small>Edit Umpires?</small>
                                </Button>
                            </Col>
                        )}
                    </Row>
                    <Row className={classNames("mt-4")}>
                        <Col>
                            <small>
                                Clicking a team or umpire name will show the
                                coach/umpire contact info
                            </small>
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShow(false)}>
                    Cancel
                </Button>
                {AUTH_LEVELS.sitemanagement.includes(user.userLevel) && (
                    <Button
                        variant="danger"
                        onClick={() => {
                            setShow(false);
                            setEventToDelete({
                                id: gameId,
                                start,
                                end,
                                title,
                                resourceId,
                                ageLevel,
                            });
                            setShowDelete(true);
                        }}
                    >
                        Delete Game
                    </Button>
                )}
                <Button variant="success" onClick={handleSubmit}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditGameModal;
